import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getHostsByCategory, hosts } from '@/mocks/hosts';
import type { JSX } from 'react';

interface Host {
  id: number;
  name: string;
  avatar: string;
  cover: string;
  category: string;
  tags: string[];
  viewers: string;
  followers: string;
  description: string;
  isOnline: boolean;
  rating: number;
}

export default function Categories(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'talent';
  const [filteredHosts, setFilteredHosts] = useState<Host[]>([]);
  const [animateKey, setAnimateKey] = useState(0);

  const currentCategory = categories.find((c) => c.id === activeTab);

  useEffect(() => {
    setAnimateKey((prev) => prev + 1);
    if (activeTab === 'all') {
      setFilteredHosts(hosts);
    } else {
      const categoryName = categories.find((c) => c.id === activeTab)?.name || '';
      setFilteredHosts(getHostsByCategory(categoryName));
    }
  }, [activeTab]);

  const handleTabChange = (catId: string) => {
    setSearchParams({ tab: catId });
  };

  return (
    <>
      <PageMeta
        title={`${currentCategory?.name || '全部'}主播 - 美女直播平台分类浏览`}
        description={`浏览${currentCategory?.name || '全部'}美女直播分类，发现高颜值${currentCategory?.name || ''}主播。才艺表演、游戏陪玩、生活分享等多元内容。`}
        keywords={`美女直播,${currentCategory?.name || ''}主播,直播分类,高颜值主播`}
        canonicalUrl={`https://live-platform.example.com/categories?tab=${activeTab}`}
      />

      <div className="pt-20 pb-16 md:pb-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '分类浏览' },
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              主播分类浏览
            </h1>
            <p className="text-foreground-600 text-base max-w-2xl">
              按分类探索你感兴趣的主播，从才艺表演到游戏陪玩，找到属于你的精彩内容
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10">
            <button
              onClick={() => handleTabChange('talent')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeTab === 'talent'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className="ri-music-2-line mr-1.5"></i>才艺表演
            </button>
            <button
              onClick={() => handleTabChange('gaming')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeTab === 'gaming'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className="ri-gamepad-line mr-1.5"></i>游戏陪玩
            </button>
            <button
              onClick={() => handleTabChange('lifestyle')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeTab === 'lifestyle'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className="ri-heart-line mr-1.5"></i>生活分享
            </button>
            <button
              onClick={() => handleTabChange('chat')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeTab === 'chat'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className="ri-chat-smile-2-line mr-1.5"></i>聊天互动
            </button>
          </div>

          {currentCategory && (
            <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-primary-500 text-background-50 shrink-0">
                  <i className={`${currentCategory.icon} text-xl md:text-2xl`}></i>
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-foreground-950 text-xl mb-1">
                    {currentCategory.name}
                  </h2>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {currentCategory.description}
                  </p>
                  <p className="text-foreground-400 text-xs mt-2">
                    共 {filteredHosts.length} 位主播
                  </p>
                </div>
              </div>
            </div>
          )}

          <div key={animateKey} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 animate-fadeIn" data-product-shop="">
            {filteredHosts.map((host: Host) => (
              <div
                key={host.id}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300"
              >
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img
                    src={host.cover}
                    alt={host.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {host.isOnline && (
                    <span className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-500 text-white text-xs font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                      直播中
                    </span>
                  )}
                  <span className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/50 text-white text-xs backdrop-blur-sm">
                    <i className="ri-eye-line text-[10px]"></i>
                    {host.viewers}
                  </span>
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={host.avatar} alt={host.name} className="w-10 h-10 md:w-11 md:h-11 rounded-full object-cover border-2 border-background-100" />
                    <div>
                      <h3 className="font-heading font-semibold text-foreground-950 text-base">{host.name}</h3>
                      <p className="text-foreground-500 text-xs">{host.category} · {host.followers}粉丝</p>
                    </div>
                    <span className="ml-auto flex items-center gap-0.5 text-accent-500 text-sm font-medium">
                      <i className="ri-star-fill text-xs"></i>
                      {host.rating}
                    </span>
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2 mb-3">{host.description}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {host.tags.map((tag: string) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredHosts.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-user-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-base">该分类暂无主播</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}